import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import SearchPage from "@/pages/SearchPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import InstallPrompt from "@/components/InstallPrompt";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

// Lazy-loaded heavy routes — split out of the main bundle so the first paint
// (home + search) ships ~40% less JS.
const ListingDetailPage = lazy(() => import("@/pages/ListingDetailPage"));
const CreateListingPage = lazy(() => import("@/pages/CreateListingPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const MyListingsPage = lazy(() => import("@/pages/MyListingsPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const BureauPage = lazy(() => import("@/pages/BureauPage"));
const AgencyPage = lazy(() => import("@/pages/AgencyPage"));
const AlertsPage = lazy(() => import("@/pages/AlertsPage"));

const prefetchRoutes = () => {
  import("@/pages/ListingDetailPage");
  import("@/pages/MessagesPage");
  import("@/pages/FavoritesPage");
};
if (typeof window !== "undefined") {
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 2500));
  window.addEventListener("load", () => idle(prefetchRoutes), { once: true });
}

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/30 border-t-primary" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // Synchronous check for OAuth callback fragment.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <AppLayout>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/rechercher" element={<SearchPage />} />
          <Route path="/annonce/:id" element={<ListingDetailPage />} />
          <Route path="/publier" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
          <Route path="/modifier/:id" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
          <Route path="/agence/:agentId" element={<AgencyPage />} />
          <Route path="/alertes" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/profil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/favoris" element={<FavoritesPage />} />
          <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/messages/:conversationId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/mes-annonces" element={<ProtectedRoute><MyListingsPage /></ProtectedRoute>} />
          <Route path="/bureau" element={<ProtectedRoute><BureauPage /></ProtectedRoute>} />
          <Route path="/tarifs" element={<PricingPage />} />
          <Route path="/paiement/succes" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
          <Route path="/paiement/annule" element={<PaymentCancelPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <InstallPrompt />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
